import { z } from "zod";
import { Id, IsoDateTime } from "./common";
import { LocationSource, LocationConfidence } from "./facility";
import { AssetStatus, MaintenanceState } from "./asset";
import { DeviceStatus } from "./device";
import { DataQuality } from "./profile";

// ---------------------------------------------------------------------------
// Operational enums
// ---------------------------------------------------------------------------

export const OnlineState = z.enum(["Online", "Offline", "Unknown"]);
export type OnlineState = z.infer<typeof OnlineState>;

export const BoundaryState = z.enum(["Inside", "Outside", "Unknown"]);
export type BoundaryState = z.infer<typeof BoundaryState>;

export const RiskLevel = z.enum(["Low", "Medium", "High", "Critical"]);
export type RiskLevel = z.infer<typeof RiskLevel>;

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

export const LatLng = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
});
export type LatLng = z.infer<typeof LatLng>;

const NonNegInt = z.number().int().min(0);
const NullableNum = z.number().nullable();

// ---------------------------------------------------------------------------
// AssetCurrentState  (§7.10 of physical data design)
// ---------------------------------------------------------------------------

export const AssetCurrentState = z.object({
  assetId: Id,
  assetName: z.string().min(1),
  tenantId: Id,
  siteId: Id,
  buildingId: Id.nullable(),
  floorId: Id.nullable(),
  assetStatus: AssetStatus,
  maintenanceState: MaintenanceState,
  riskLevel: RiskLevel,
  riskScore: z.number().min(0).max(100),
  riskReasons: z.array(z.string()),
  onlineState: OnlineState,
  lastSeenAt: IsoDateTime.nullable(),
  lastTelemetryAt: IsoDateTime.nullable(),
  lastLocationAt: IsoDateTime.nullable(),
  latitude: NullableNum,
  longitude: NullableNum,
  locationSource: LocationSource.nullable(),
  locationConfidence: LocationConfidence.nullable(),
  boundaryState: BoundaryState,
  activeAlertCount: NonNegInt,
  criticalAlertCount: NonNegInt,
  openWorkItemCount: NonNegInt,
  assignedDeviceCount: NonNegInt,
  onlineDeviceCount: NonNegInt,
  offlineDeviceCount: NonNegInt,
  latestTelemetrySummary: z.record(z.unknown()).nullable(),
  /** Data quality indicator derived from polling staleness and profile thresholds. */
  dataQuality: DataQuality.optional(),
  updatedAt: IsoDateTime,
});
export type AssetCurrentState = z.infer<typeof AssetCurrentState>;

// ---------------------------------------------------------------------------
// DeviceCurrentState  (§7.13)
// ---------------------------------------------------------------------------

export const DeviceCurrentState = z.object({
  deviceId: Id,
  tenantId: Id,
  assetId: Id.nullable(),
  siteId: Id.nullable(),
  deviceStatus: DeviceStatus,
  onlineState: OnlineState,
  lastSeenAt: IsoDateTime.nullable(),
  lastTelemetryAt: IsoDateTime.nullable(),
  lastDiagnosticAt: IsoDateTime.nullable(),
  batteryLevel: NullableNum,
  signalStrength: NullableNum,
  firmwareVersion: z.string().nullable(),
  faultCode: z.string().nullable(),
  faultMessage: z.string().nullable(),
  maintenanceState: MaintenanceState,
  riskLevel: RiskLevel,
  riskScore: z.number().min(0).max(100),
  riskReasons: z.array(z.string()),
  activeAlertCount: NonNegInt,
  latitude: NullableNum,
  longitude: NullableNum,
  locationSource: LocationSource.nullable(),
  locationConfidence: LocationConfidence.nullable(),
  latestTelemetrySummary: z.record(z.unknown()).nullable(),
  /** Data quality indicator derived from polling staleness and profile thresholds. */
  dataQuality: DataQuality.optional(),
  updatedAt: IsoDateTime,
});
export type DeviceCurrentState = z.infer<typeof DeviceCurrentState>;

// ---------------------------------------------------------------------------
// SiteCurrentState  (§7.14)
// ---------------------------------------------------------------------------

const IntegrationHealthSummary = z.object({
  Healthy: NonNegInt,
  Degraded: NonNegInt,
  Failing: NonNegInt,
});

const WeatherRiskSummary = z.object({
  riskLevel: RiskLevel,
  summary: z.string(),
});

export const SiteCurrentState = z.object({
  siteId: Id,
  tenantId: Id,
  riskLevel: RiskLevel,
  riskScore: z.number().min(0).max(100),
  riskReasons: z.array(z.string()),
  totalAssetCount: NonNegInt,
  highRiskAssetCount: NonNegInt,
  criticalRiskAssetCount: NonNegInt,
  offlineDeviceCount: NonNegInt,
  activeAlertCount: NonNegInt,
  criticalAlertCount: NonNegInt,
  openWorkItemCount: NonNegInt,
  integrationHealthSummary: IntegrationHealthSummary,
  weatherRiskSummary: WeatherRiskSummary.nullable(),
  updatedAt: IsoDateTime,
  calculationVersion: z.string(),
});
export type SiteCurrentState = z.infer<typeof SiteCurrentState>;

// ---------------------------------------------------------------------------
// SiteBoundary
// ---------------------------------------------------------------------------

export const SiteBoundary = z.object({
  siteId: Id,
  tenantId: Id,
  polygon: z.array(LatLng).min(3),
  updatedAt: IsoDateTime,
  updatedBy: Id.nullable(),
});
export type SiteBoundary = z.infer<typeof SiteBoundary>;

export const SiteBoundaryUpdateInput = z.object({
  polygon: z.array(LatLng).min(3),
});
export type SiteBoundaryUpdateInput = z.infer<typeof SiteBoundaryUpdateInput>;

// ---------------------------------------------------------------------------
// Dashboard DTOs  (§7.2 of service API design)
// ---------------------------------------------------------------------------

const DashboardCards = z.object({
  totalAssets: NonNegInt,
  onlineDevices: NonNegInt,
  offlineDevices: NonNegInt,
  assetsWithAlerts: NonNegInt,
  openWorkItems: NonNegInt,
  assetsMissingRecentData: NonNegInt,
});

const DashboardRiskSummary = z.object({
  criticalAssets: NonNegInt,
  highRiskAssets: NonNegInt,
  mediumRiskAssets: NonNegInt,
  lowRiskAssets: NonNegInt,
  highestRiskSites: z.array(
    z.object({
      siteId: Id,
      name: z.string(),
      riskLevel: RiskLevel,
      riskScore: z.number(),
    }),
  ),
});

const DashboardIntegrationHealth = z.object({
  healthy: NonNegInt,
  degraded: NonNegInt,
  failing: NonNegInt,
  disabled: NonNegInt,
});

export const DashboardRecentAlert = z.object({
  alertId: Id,
  title: z.string(),
  severity: z.enum(["Critical", "High", "Medium", "Low", "Info"]),
  status: z.enum(["Active", "Acknowledged", "Dismissed", "Resolved"]),
  siteId: Id.nullable(),
  assetId: Id.nullable(),
  deviceId: Id.nullable(),
  createdAt: IsoDateTime,
});
export type DashboardRecentAlert = z.infer<typeof DashboardRecentAlert>;

export const DashboardHighestRiskAsset = z.object({
  assetId: Id,
  name: z.string(),
  siteId: Id,
  riskLevel: RiskLevel,
  riskScore: z.number().min(0).max(100),
  activeAlertCount: NonNegInt,
  openWorkItemCount: NonNegInt,
});
export type DashboardHighestRiskAsset = z.infer<typeof DashboardHighestRiskAsset>;

export const DashboardMaintenanceDistribution = z.object({
  Open: NonNegInt,
  InProgress: NonNegInt,
  OnHold: NonNegInt,
  Resolved: NonNegInt,
  Closed: NonNegInt,
});
export type DashboardMaintenanceDistribution = z.infer<typeof DashboardMaintenanceDistribution>;

export const DashboardSummary = z.object({
  tenantId: Id,
  generatedAt: IsoDateTime,
  cards: DashboardCards,
  risk: DashboardRiskSummary,
  integrationHealth: DashboardIntegrationHealth,
  recentAlerts: z.array(DashboardRecentAlert).optional(),
  highestRiskAssets: z.array(DashboardHighestRiskAsset).optional(),
  recentlyUpdatedAssets: z.array(DashboardHighestRiskAsset).optional(),
  maintenanceDistribution: DashboardMaintenanceDistribution.optional(),
  staleDeviceCount: NonNegInt.optional(),
});
export type DashboardSummary = z.infer<typeof DashboardSummary>;

// ---------------------------------------------------------------------------
// Map DTOs  (§7.3 of service API design)
// ---------------------------------------------------------------------------

export const MapAssetMarker = z.object({
  assetId: Id,
  name: z.string(),
  assetType: z.string(),
  siteId: Id,
  buildingId: Id.nullable(),
  floorId: Id.nullable(),
  latitude: NullableNum,
  longitude: NullableNum,
  locationSource: LocationSource.nullable(),
  locationConfidence: LocationConfidence.nullable(),
  boundaryState: BoundaryState,
  onlineState: OnlineState,
  maintenanceState: MaintenanceState,
  riskLevel: RiskLevel,
  lastSeenAt: IsoDateTime.nullable(),
  activeAlertCount: NonNegInt,
  openWorkItemCount: NonNegInt,
});
export type MapAssetMarker = z.infer<typeof MapAssetMarker>;

export const MapSite = z.object({
  siteId: Id,
  tenantId: Id,
  name: z.string(),
  latitude: NullableNum,
  longitude: NullableNum,
  locationSource: LocationSource,
  locationConfidence: LocationConfidence,
  riskLevel: RiskLevel,
  riskScore: z.number(),
  totalAssetCount: NonNegInt,
  activeAlertCount: NonNegInt,
  offlineDeviceCount: NonNegInt,
  hasBoundary: z.boolean(),
});
export type MapSite = z.infer<typeof MapSite>;
